import React, { useState, useEffect, useRef } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { propertyAPI, catalogAPI } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  ImagePlus,
  Trash2,
  Save,
  Upload,
  Wifi,
  Car,
  Waves,
  Dumbbell,
  Wind,
  Coffee,
  Tv,
  ShieldCheck,
  UtensilsCrossed,
  Droplets,
  Flame,
  ParkingCircle,
  Dog,
  Baby,
  Loader2,
  X,
  MapPin,
  Users,
  FileText,
  CheckCircle2,
  BookImage
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AMENITY_OPTIONS = [
  { id: 'WiFi', label: 'WiFi', icon: Wifi },
  { id: 'Piscina', label: 'Piscina', icon: Waves },
  { id: 'Parqueadero', label: 'Parqueadero', icon: ParkingCircle },
  { id: 'Gimnasio', label: 'Gimnasio', icon: Dumbbell },
  { id: 'Aire Acondicionado', label: 'Aire Acondicionado', icon: Wind },
  { id: 'Desayuno', label: 'Desayuno', icon: Coffee },
  { id: 'TV', label: 'TV / Smart TV', icon: Tv },
  { id: 'Seguridad 24h', label: 'Seguridad 24h', icon: ShieldCheck },
  { id: 'Restaurante', label: 'Restaurante', icon: UtensilsCrossed },
  { id: 'Agua Caliente', label: 'Agua Caliente', icon: Droplets },
  { id: 'Cocina', label: 'Cocina', icon: Flame },
  { id: 'Pet Friendly', label: 'Pet Friendly', icon: Dog },
  { id: 'Niños', label: 'Apto para niños', icon: Baby },
];

import { useGlobalProperty } from '@/hooks/useGlobalProperty';

export default function CatalogPage() {
  const { user } = useAuth();
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useGlobalProperty(properties);
  const [catalog, setCatalog] = useState({
    amenities: [],
    description: '',
    capacity: 0,
    location_details: '',
    photos: []
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [customAmenity, setCustomAmenity] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => { loadProperties(); }, []);
  useEffect(() => { if (selectedProperty) loadCatalog(selectedProperty); }, [selectedProperty]);

  const loadProperties = async () => {
    try {
      const res = await propertyAPI.getAll();
      setProperties(res.data);
      
    } catch { toast.error('Error cargando propiedades'); }
  };

  const loadCatalog = async (propId) => {
    try {
      const res = await catalogAPI.get(propId);
      setCatalog({
        amenities: res.data.amenities || [],
        description: res.data.description || '',
        capacity: res.data.capacity || 0,
        location_details: res.data.location_details || '',
        photos: res.data.photos || []
      });
    } catch {
      setCatalog({ amenities: [], description: '', capacity: 0, location_details: '', photos: [] });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await catalogAPI.save(selectedProperty, {
        amenities: catalog.amenities,
        description: catalog.description,
        capacity: catalog.capacity,
        location_details: catalog.location_details
      });
      toast.success('Catálogo guardado correctamente');
    } catch { toast.error('Error al guardar catálogo'); }
    finally { setSaving(false); }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name} supera 10MB`);
          continue;
        }
        const res = await catalogAPI.uploadPhoto(selectedProperty, file);
        setCatalog(prev => ({
          ...prev,
          photos: [...prev.photos, { id: res.data.photo_id, url: res.data.url, filename: file.name }]
        }));
      }
      toast.success(`${files.length} foto(s) subida(s)`);
    } catch { toast.error('Error al subir fotos'); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      await catalogAPI.deletePhoto(selectedProperty, photoId);
      setCatalog(prev => ({ ...prev, photos: prev.photos.filter(p => p.id !== photoId) }));
      toast.success('Foto eliminada');
    } catch { toast.error('Error al eliminar foto'); }
  };

  const toggleAmenity = (amenityId) => {
    setCatalog(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenityId)
        ? prev.amenities.filter(a => a !== amenityId)
        : [...prev.amenities, amenityId]
    }));
  };

  const addCustomAmenity = () => {
    const val = customAmenity.trim();
    if (val && !catalog.amenities.includes(val)) {
      setCatalog(prev => ({ ...prev, amenities: [...prev.amenities, val] }));
      setCustomAmenity('');
    }
  };

  const propName = properties.find(p => p.id === selectedProperty)?.nombre || '';

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="catalog-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2" data-testid="catalog-page-title">
              <BookImage className="h-6 w-6 text-amber-600" />
              Catálogo de Propiedad
            </h1>
            <p className="text-muted-foreground mt-1">
              Fotos, amenidades e información que el chatbot de WhatsApp enviará a los interesados
            </p>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="save-catalog-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Guardar Catálogo
          </Button>
        </div>

        {/* Property Selector */}
        {properties.length > 1 && (
          <div className="flex items-center gap-3">
            <Label className="text-sm font-medium whitespace-nowrap">Propiedad:</Label>
            <select
              value={selectedProperty}
              onChange={(e) => setSelectedProperty(e.target.value)}
              className="border rounded-md px-3 py-2 text-sm bg-background"
              data-testid="catalog-property-selector"
            >
              {properties.map(p => (
                <option key={p.id} value={p.id}>{p.nombre}</option>
              ))}
            </select>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column: Photos */}
          <div className="lg:col-span-2 space-y-6">
            <Card data-testid="catalog-photos-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <ImagePlus className="h-5 w-5 text-amber-600" />
                  Fotos de la Propiedad
                  <Badge variant="outline" className="ml-auto">{catalog.photos.length} foto(s)</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Upload area */}
                <div
                  className="border-2 border-dashed border-amber-300 rounded-lg p-8 text-center cursor-pointer hover:border-amber-500 hover:bg-amber-50/50 transition-all"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="photo-upload-area"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    onChange={handlePhotoUpload}
                    className="hidden"
                    data-testid="photo-file-input"
                  />
                  {uploading ? (
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="h-8 w-8 text-amber-600 animate-spin" />
                      <p className="text-sm text-muted-foreground">Subiendo fotos...</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Upload className="h-8 w-8 text-amber-400" />
                      <p className="text-sm font-medium">Haz clic para subir fotos</p>
                      <p className="text-xs text-muted-foreground">JPG, PNG o WEBP (máx. 10MB)</p>
                    </div>
                  )}
                </div>

                {/* Photo grid */}
                {catalog.photos.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {catalog.photos.map((photo) => (
                      <div key={photo.id} className="relative group rounded-lg overflow-hidden border" data-testid={`photo-${photo.id}`}>
                        <img
                          src={`${BACKEND_URL}${photo.url}`}
                          alt={photo.filename || 'Foto propiedad'}
                          className="w-full h-36 object-cover"
                          onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="144" fill="%23f3f4f6"><rect width="200" height="144"/><text x="50%" y="50%" font-size="12" fill="%239ca3af" text-anchor="middle" dy=".3em">Sin imagen</text></svg>'; }}
                        />
                        <button
                          onClick={() => handleDeletePhoto(photo.id)}
                          className="absolute top-1.5 right-1.5 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`delete-photo-${photo.id}`}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Description & Location */}
            <Card data-testid="catalog-info-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="h-5 w-5 text-amber-600" />
                  Descripción e Información
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Descripción de la propiedad</Label>
                  <Textarea
                    rows={4}
                    placeholder="Describe tu propiedad: tipo de alojamiento, lo que la hace especial, vistas, ambiente..."
                    value={catalog.description}
                    onChange={(e) => setCatalog(prev => ({ ...prev, description: e.target.value }))}
                    data-testid="catalog-description"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1.5">
                      <Users className="h-4 w-4" />
                      Capacidad máxima (personas)
                    </Label>
                    <Input
                      type="number"
                      min="1"
                      placeholder="Ej: 6"
                      value={catalog.capacity || ''}
                      onChange={(e) => setCatalog(prev => ({ ...prev, capacity: parseInt(e.target.value) || 0 }))}
                      data-testid="catalog-capacity"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1.5">
                      <MapPin className="h-4 w-4" />
                      Detalles de ubicación
                    </Label>
                    <Input
                      placeholder="Ej: A 5 min de la playa, centro histórico"
                      value={catalog.location_details}
                      onChange={(e) => setCatalog(prev => ({ ...prev, location_details: e.target.value }))}
                      data-testid="catalog-location"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right column: Amenities */}
          <div className="space-y-6">
            <Card data-testid="catalog-amenities-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-amber-600" />
                  Amenidades
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 gap-2">
                  {AMENITY_OPTIONS.map(({ id, label, icon: Icon }) => {
                    const isActive = catalog.amenities.includes(id);
                    return (
                      <button
                        key={id}
                        onClick={() => toggleAmenity(id)}
                        className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border text-left transition-all text-sm ${
                          isActive
                            ? 'bg-amber-50 border-amber-400 text-amber-800'
                            : 'bg-background border-gray-200 text-muted-foreground hover:border-amber-300'
                        }`}
                        data-testid={`amenity-${id}`}
                      >
                        <Icon className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-amber-600' : 'text-gray-400'}`} />
                        <span className="flex-1">{label}</span>
                        {isActive && <CheckCircle2 className="h-4 w-4 text-amber-600 flex-shrink-0" />}
                      </button>
                    );
                  })}
                </div>

                {/* Custom amenity */}
                <div className="pt-3 border-t">
                  <Label className="text-xs text-muted-foreground mb-1.5 block">Amenidad personalizada</Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Ej: Jacuzzi"
                      value={customAmenity}
                      onChange={(e) => setCustomAmenity(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && addCustomAmenity()}
                      className="text-sm"
                      data-testid="custom-amenity-input"
                    />
                    <Button size="sm" variant="outline" onClick={addCustomAmenity} data-testid="add-custom-amenity-btn">+</Button>
                  </div>
                </div>

                {/* Custom amenities listed */}
                {catalog.amenities.filter(a => !AMENITY_OPTIONS.some(o => o.id === a)).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pt-2">
                    {catalog.amenities.filter(a => !AMENITY_OPTIONS.some(o => o.id === a)).map(a => (
                      <Badge key={a} variant="secondary" className="flex items-center gap-1">
                        {a}
                        <X className="h-3 w-3 cursor-pointer" onClick={() => toggleAmenity(a)} />
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Preview info */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-muted-foreground">Vista previa en WhatsApp</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-[#e5ddd5] rounded-lg p-3 text-sm space-y-2">
                  <div className="bg-white rounded-lg px-3 py-2 shadow-sm">
                    <p className="font-bold">{propName || 'Tu Propiedad'}</p>
                    {catalog.capacity > 0 && <p>Capacidad: {catalog.capacity} personas</p>}
                    {catalog.amenities.length > 0 && <p>Amenidades: {catalog.amenities.join(', ')}</p>}
                    {catalog.location_details && <p>Detalles: {catalog.location_details}</p>}
                    {catalog.description && <p className="text-gray-600 text-xs mt-1">{catalog.description.slice(0, 80)}...</p>}
                    {catalog.photos.length > 0 && (
                      <p className="text-green-600 text-xs mt-1">+ {catalog.photos.length} foto(s) adjuntas</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
